import { MeasureWrapper } from '@/components/preview/MeasureWrapper';
import {
    DEFAULT_SECTION_HEIGHT,
    DEFAULT_TITLE_HEIGHT,
} from '@/components/preview/pagination/constants';
import { DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR } from '@/constants';
import { isChartSpecEmpty, resolveCanvasSize, resolveChartSpec } from '@/lib/chart-config';
import { toNormalizedTableView } from '@/lib/table-chart';
import { getTextColorForBackground } from '@/utils/getTextColorForBackground';
import { resolvePreviewUrl } from '@/utils/preview-cache';
import { chartBoxStyle } from '../chart-resize/chart-size';
import ChartRenderer from '../ChartRenderer';
import { BLOCK_TEXT_SPACING_RESET } from '../constants';
import { getFootnoteBlockId } from '../footnotes';
import { imageBoxStyle } from '../image-box';
import { getBlockMeasurementId, isSplitBlockId, stripSplitBlockId } from '../pagination/utils';
import StandaloneRichText from './StandaloneRichText';
import StandaloneTableRenderer from './StandaloneTableRenderer';
import type { ReportFootnotePlan } from '../footnotes';
import type { ReportData } from '../types';
import type { DividerCustomizations } from '@/types/report';
import type {
    ChartBlock,
    DividerBlock,
    ImageBlock,
    TableBlock,
    TestBlock,
    TextBlock,
} from '@/types/report-payload';
import type { ReactNode } from 'react';

interface StandaloneTestsRendererProps {
    data: ReportData;
    blocks?: TestBlock[];
    measureBlocks?: boolean;
    footnotePlan?: ReportFootnotePlan;
}

function StandaloneChartBlock({ block, data }: { block: ChartBlock; data: ReportData }) {
    const linkedTableBlock =
        block.linked_table_id &&
        (data.tests?.blocks || []).find(
            (candidate): candidate is TableBlock =>
                candidate.block_type === 'table' && candidate.id === block.linked_table_id,
        );

    // Brand colors seed the default series colors (explicit picks still win),
    // matching the report editor and the .docx raster.
    const spec = resolveChartSpec(block, {
        table: linkedTableBlock ? toNormalizedTableView(linkedTableBlock) : null,
        primaryColor: data.customizations?.colors?.primary ?? DEFAULT_PRIMARY_COLOR,
        secondaryColor: data.customizations?.colors?.secondary ?? DEFAULT_SECONDARY_COLOR,
    });

    const isEmpty = isChartSpecEmpty(spec);
    if (isEmpty) {
        return (
            <div className="mb-6 flex min-h-[180px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-sm text-slate-500">
                Gráfico sem dados configurados
            </div>
        );
    }

    const box = chartBoxStyle(block.size?.width_pct, resolveCanvasSize(spec));
    return (
        <div className={`relative ${box.className}`} style={box.style}>
            <ChartRenderer spec={spec} />
        </div>
    );
}

function StandaloneImageBlock({ block }: { block: ImageBlock }) {
    if (!block.ref) return null;

    // The box owns the height, taken from the recorded natural size, so the
    // measured height matches the rendered one whether or not the image has
    // loaded yet.
    const box = imageBoxStyle(block.size?.width_pct, block.intrinsic ?? { width: 4, height: 3 });

    // Undefined when the stored reference reached us unsigned. The box stays
    // either way — it carries the recorded height the measurement relies on.
    const src = resolvePreviewUrl(block.ref);

    return (
        <div className="relative" style={box.style}>
            {src && <img src={src} alt="" className="size-full object-contain" />}
        </div>
    );
}

function StandaloneDividerBlock({
    block,
    primaryColor,
    dividerConfig,
}: {
    block: DividerBlock;
    primaryColor: string;
    dividerConfig?: DividerCustomizations;
}) {
    const alignment = dividerConfig?.alignment || 'center';
    const showLine = dividerConfig?.show_line !== false;
    const fontStyle = dividerConfig?.font_style || 'normal';
    const rawBgColor = dividerConfig?.background_color ?? 'primary';
    const resolvedBgColor = rawBgColor === 'primary' ? primaryColor : rawBgColor || undefined;
    const textColor = dividerConfig?.font_color
        ? dividerConfig.font_color
        : resolvedBgColor
          ? getTextColorForBackground(resolvedBgColor)
          : undefined;

    return (
        <div
            style={
                resolvedBgColor
                    ? {
                          backgroundColor: resolvedBgColor,
                          padding: '8px 12px',
                          borderRadius: '4px',
                      }
                    : undefined
            }
        >
            <h3
                className={`text-base ${!textColor ? 'text-slate-700' : ''}`}
                style={{
                    textAlign: alignment,
                    fontWeight: fontStyle === 'bold' ? 'bold' : 'normal',
                    fontStyle: fontStyle === 'italic' ? 'italic' : 'normal',
                    ...(textColor ? { color: textColor } : {}),
                }}
            >
                {block.title}
            </h3>
            {showLine && <hr style={{ borderColor: primaryColor }} className="mt-1 border-t" />}
            {block.description?.trim() && (
                <p className="mt-1 text-sm text-slate-500">{block.description}</p>
            )}
        </div>
    );
}

export default function StandaloneTestsRenderer({
    data,
    blocks,
    measureBlocks = false,
    footnotePlan,
}: StandaloneTestsRendererProps) {
    const testBlocks = blocks || data.tests?.blocks || [];
    const primaryColor = data.customizations?.colors?.primary || '#01026b';
    const dividerConfig = data.customizations?.divider;

    const resolveLinkedTableBlock = (block: TextBlock): TableBlock | undefined => {
        const linkedId = block.linked_table_id;

        if (!linkedId) return undefined;

        return (data.tests?.blocks || []).find(
            (candidate): candidate is TableBlock =>
                candidate.block_type === 'table' &&
                stripSplitBlockId(candidate.id) === stripSplitBlockId(linkedId),
        );
    };

    if (testBlocks.length === 0) {
        return (
            <div
                className="flex items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-sm text-slate-500"
                style={{ minHeight: DEFAULT_SECTION_HEIGHT }}
            >
                Sem blocos configurados
            </div>
        );
    }

    return (
        <div
            className={`space-y-5 ${BLOCK_TEXT_SPACING_RESET}`}
            style={{ minHeight: DEFAULT_TITLE_HEIGHT }}
        >
            {testBlocks.map((block) => {
                const uniqueKey = (block.id || block.order).toString();

                let content: ReactNode = null;

                if (block.block_type === 'table') {
                    content = (
                        <StandaloneTableRenderer
                            block={block}
                            data={data}
                            footnotes={footnotePlan?.byBlockId[getFootnoteBlockId(block)]}
                        />
                    );
                }

                if (block.block_type === 'chart') {
                    content = <StandaloneChartBlock block={block} data={data} />;
                }

                if (block.block_type === 'text') {
                    const linkedTableBlock = resolveLinkedTableBlock(block);
                    const isLinkedTextBlock = !!block.linked_table_id;
                    const isSplitContinuation = isSplitBlockId(block.id);
                    const shouldHideOrphanLinkedTextBlock =
                        isLinkedTextBlock && !linkedTableBlock && !isSplitContinuation;
                    const shouldHideLinkedTextBlock =
                        isLinkedTextBlock &&
                        !shouldHideOrphanLinkedTextBlock &&
                        !block.content?.trim();

                    if (
                        !shouldHideOrphanLinkedTextBlock &&
                        !shouldHideLinkedTextBlock &&
                        block.content?.trim()
                    ) {
                        content = <StandaloneRichText content={block.content} />;
                    }
                }

                if (block.block_type === 'image') {
                    content = <StandaloneImageBlock block={block} />;
                }

                if (block.block_type === 'divider') {
                    content = (
                        <StandaloneDividerBlock
                            block={block}
                            primaryColor={primaryColor}
                            dividerConfig={dividerConfig}
                        />
                    );
                }

                if (!content) return null;

                return measureBlocks ? (
                    <MeasureWrapper key={uniqueKey} id={getBlockMeasurementId(uniqueKey)}>
                        {content}
                    </MeasureWrapper>
                ) : (
                    <div key={uniqueKey}>{content}</div>
                );
            })}
        </div>
    );
}
